// Rebook nudge (owner's Rebook screen) — the pure half. One tap opens WhatsApp
// on the customer's number with the message already typed; Roundly never
// sends anything itself (same rule as Running late). The wording lives here
// and nowhere else so the owner can tune it without touching the screen, and
// a test pins it verbatim.

import { cleanPhone, firstName } from './late-message'
import { callingCode, messageClock, monthFirst, roundDay } from './round-config'
import { WINBACK_DISCOUNT_PERCENT, askAheadDays, businessSettings, setTimesOn, visibleTime, whatsappStopped, wordMessage, type BusinessSettings } from './business-settings'

// First-message wording, by how late they are:
//   due     — under four weeks past due: "due a clean around now"
//   overdue — four weeks or more, but not yet going cold: an apology
//   cold    — more than two full cycles: "it's been a while"
//   away    — no visit for over eight months: asks whether they want to start
//             again at all, and offers NO day
//   winback — the same question to someone already OFF the list (lapsed), with
//             money off the first clean back
// 'slipping' is kept as a name for 'due' so older callers still work.
export type RebookKind = 'due' | 'slipping' | 'overdue' | 'cold' | 'upcoming' | 'away' | 'winback' | 'six_months'

// What comes off the first clean when asking a lapsed customer back (Liam,
// 2026-09-16). Only ever offered to someone already off the list: a customer
// still on the round is asked back for nothing, because nothing was lost.
// The win-back offer's percentage lives with the message wording (message-wording.ts); kept exported here too.
export { WINBACK_DISCOUNT_PERCENT }

// Coming up: due within the next week, nothing agreed yet. These are the
// customers who are always booked ahead, so the nudge goes out before the
// date, not after. A week is the standard; the owner can make it two, three or
// four (More > Your messages, "How far ahead to ask", Liam 9 Oct 2026), and
// comingUpDays() is what Messages, its count and Book a visit's "Message them"
// read.
export const COMING_UP_DAYS = 7
export function comingUpDays(): number {
  return askAheadDays()
}

export const OVERDUE_APOLOGY_AFTER_DAYS = 28

// Eight months with no visit is a different conversation (Liam 2026-09-16,
// looking at Kyri Petrou: last clean April 2025). "Would Thursday work for
// you?" asks someone who has not seen us for the better part of a year to
// commit to a date; the honest question is whether they want us back at all,
// and the day comes in their reply. Counted in CALENDAR months, so "over eight
// months" means what it says in any month length.
export const AWAY_AFTER_MONTHS = 8

// Months between two dates, by the calendar: 2025-04-24 → 2026-09-16 is 16,
// not "about 510 days". Day-of-month decides the last one, so 8 months and a
// day counts as 8 and 8 months less a day does not.
// "Now" is the ROUND's day (round-config.ts, Step 1), so a phone in another zone agrees with the
// server about who has been away long enough (Astra review, 29 Sep 2026).
// "6 months", "a year", "over a year", "2 years": how long since the last clean, as the customer reads it.
export function timeSinceLabel(iso: string | null | undefined, now: Date = new Date()): string {
  const months = monthsSince(iso, now)
  if (months === null) return 'a while'
  if (months < 2) return 'a month'
  if (months < 12) return `${months} months`
  if (months === 12) return 'a year'
  if (months < 24) return 'over a year'
  return `${Math.floor(months / 12)} years`
}

export function monthsSince(iso: string | null | undefined, now: Date = new Date()): number | null {
  if (!iso) return null
  const then = new Date(`${iso}T12:00:00`)
  if (Number.isNaN(then.getTime())) return null
  const [ny, nm, nd] = roundDay(now).split('-').map(Number) as [number, number, number]
  let months = (ny - then.getFullYear()) * 12 + (nm - 1 - then.getMonth())
  if (nd < then.getDate()) months -= 1
  return months
}

// Has this customer been away long enough for the different message? Only ever
// asked about someone already overdue: a customer on a yearly cycle who is due
// next week has been away eleven months and is not lost, they are on time.
export function longAway(lastClean: string | null | undefined, now: Date = new Date()): boolean {
  const months = monthsSince(lastClean, now)
  return months !== null && months >= AWAY_AFTER_MONTHS
}

// A customer cleaned every 6 months or once a year gets their own first message (rebook_six_months: "It's been
// 6 months since your last clean"), due, slipped or long away alike: for them a long gap is the normal rhythm, not a
// lapse (Liam, 8 Oct 2026). Two whole cycles missed (cold) goes back to the usual cold or long-away message.
export const LONG_CYCLE_DAYS = 180
export function firstMessageKind(overdueDays: number, cold: boolean, away = false, longCycle = false): RebookKind {
  if (longCycle && !cold) return 'six_months'
  if (away) return 'away'
  if (cold) return 'cold'
  return overdueDays >= OVERDUE_APOLOGY_AFTER_DAYS ? 'overdue' : 'due'
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

// "2026-05-15" → "May" (+ year when it is not this year: "May 2025"). The
// customer reads this, so it is the month's full name, never "05".
export function monthLabel(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return ''
  const m = /^(\d{4})-(\d{2})/.exec(iso)
  if (!m) return ''
  const year = Number(m[1])
  const month = MONTHS[Number(m[2]) - 1]
  if (!month) return ''
  return year === Number(roundDay(now).slice(0, 4)) ? month : `${month} ${year}` // the round's year (Step 1)
}

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const SHORT_MONTHS = MONTHS.map((m) => m.slice(0, 3))

// "2026-09-22" -> "Tuesday 22nd September". The customer reads this and has to
// recognise the day without checking a calendar, so it is the weekday, the
// ordinal date and the month's full name, exactly as Liam says it out loud.
export function offerDay(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso ?? ''))
  if (!m) return ''
  // Midday, so a clock change can never move the weekday.
  const d = new Date(`${iso}T12:00:00`)
  if (Number.isNaN(d.getTime())) return ''
  const day = Number(m[3])
  const month = MONTHS[Number(m[2]) - 1]
  if (!month) return ''
  // A US or Canadian round: "Tuesday, September 22" (Step 2 round 4).
  if (monthFirst()) return `${DAYS[d.getDay()]}, ${month} ${day}`
  return `${DAYS[d.getDay()]} ${day}${ordinal(day)} ${month}`
}

// 1st, 2nd, 3rd, 4th ... and the teens, which are all "th" whatever they end in.
function ordinal(day: number): string {
  if (day >= 11 && day <= 13) return 'th'
  if (day % 10 === 1) return 'st'
  if (day % 10 === 2) return 'nd'
  if (day % 10 === 3) return 'rd'
  return 'th'
}

// The closing question. The round is in an area on a day whether or not this
// customer has said yes, so the message offers THAT day rather than asking an
// open question the owner then has to negotiate against a fixed round (Liam,
// 2026-09-14: "not to ask when they want, because that's not how our business
// works"). With no day chosen it leaves "[day]" for the owner to type in, so
// an unanswered dropdown can never send an empty-sounding question.
export function dayQuestion(offer: string | null | undefined, offers?: readonly string[]): string {
  const days = [...new Set(offers ?? [])].map(offerDay).filter(Boolean)
  if (days.length === 2) return `Would ${days[0]} or ${days[1]} work for you?`
  if (days.length > 2) return `Would any of these days work for you?\n${days.map((day) => `• ${day}`).join('\n')}`
  if (days.length === 1) return `Would ${days[0]} work for you?`
  const day = offerDay(offer) || '[day]'
  return `Would ${day} work for you?`
}

// ---- choosing which day to offer --------------------------------------------

// A day of the round with somebody out on it, as the Messages dropdown needs
// it. Structural on purpose: this file stays free of the API types so the
// wording and the choosing can be tested without a payload.
export interface OfferableDay {
  date: string
  worker: string
  areas?: string[]
}

// Their area, read the way the diary's own filter reads it (schedule.ts
// areaOf): the area if they have one, otherwise the outward half of the
// postcode. The two MUST agree — a customer the diary files under N22 and
// Messages files under nothing would never be matched to their own round.
export function customerArea(c: { area?: string | null; postcode?: string | null }): string {
  return c.area?.trim() || c.postcode?.trim().split(/\s/)[0]?.toUpperCase() || ''
}

// The day the dropdown lands on before anyone touches it: the soonest day
// from today with somebody out on it, PREFERRING one already visiting their
// part of town.
//
// The area used to be required, and that was wrong on the first day the
// feature was used (Liam, 2026-09-14: Liam went on Wednesday 16th, an empty
// day, and Ruth's message still said "[day]"). A day goes on the roster
// precisely BEFORE it has visits on it — winning those visits is what these
// messages are for — so a day's areas come from bookings that do not exist
// yet, and requiring a match meant the emptiest days, the ones most needing
// filling, were the only ones the app refused to offer.
//
// So the area now ORDERS the choice rather than gating it. The owner sees
// each day's areas in the dropdown and reads the message before sending, so a
// day near them is the better default and a day far from them is still a far
// better default than none.
// Week placement is not an appointment. Match the diary's imported-week rule too.
export function plannedWeekOf(c: { next_booked?: string | null; next_booked_source?: string | null; next_booked_created_by?: string | null; next_booked_confirmed?: number | null }): string | null {
  if (!c.next_booked || c.next_booked_confirmed) return null
  const source = c.next_booked_source
  return source === 'week' || (!source && c.next_booked_created_by === 'importer') ? c.next_booked : null
}

export function defaultOfferDate(
  c: { area?: string | null; postcode?: string | null },
  days: OfferableDay[],
  today: string,
): string {
  const upcoming = days.filter((d) => d.date >= today).sort((a, b) => a.date.localeCompare(b.date))
  if (!upcoming.length) return ''
  const area = customerArea(c).toLowerCase()
  const near = area ? upcoming.find((d) => (d.areas ?? []).some((a) => a.trim().toLowerCase() === area)) : undefined
  return (near ?? upcoming[0]!).date
}

// "Tue 22 Sep · Muswell Hill · Sendi" — the day, where the round already is
// that day, and who is out. The area is what tells the owner at a glance the
// van is near this customer. shortDate is passed in (ui.shortDate) so this
// file stays free of the DOM module and every screen prints dates alike.
// named false: a one-person round (Liam, 19 Oct 2026), where every day is the owner's, so no name follows it.
export function offerLabel(d: OfferableDay, shortDate: (iso: string) => string, named = true): string {
  const areas = (d.areas ?? []).filter((a) => a.trim()).slice(0, 3).join(', ')
  return [shortDate(d.date), areas, named ? d.worker : null].filter(Boolean).join(' · ')
}

export interface RebookMessageParts {
  name: string | null | undefined
  kind: RebookKind
  lastClean: string | null | undefined
  // The date the clean was due (for the apology's "due back in March").
  due?: string | null
  // The day of the round being offered (yyyy-mm-dd), or null for "[day]".
  offer?: string | null
  offers?: readonly string[]
  now?: Date
  // Several jobs (step 2, PLAN 4a): the jobs this message is about, in the customer's words and in order, gutters
  // first ("gutters", "windows"). Left out it is the windows, word for word as before. The away and win-back
  // messages are the main job's only and never read it.
  jobs?: string[]
}

// "gutters and windows", "gutters, fascias and windows".
export function jobsPhrase(words: string[]): string {
  const w = words.filter((x) => x.trim()).map((x) => x.trim())
  if (w.length <= 1) return w[0] ?? 'windows'
  return `${w.slice(0, -1).join(', ')} and ${w[w.length - 1]}`
}

// "are" for windows and gutters, "is" for one thing that is not plural ("conservatory roof").
function areFor(words: string[]): { are: string; were: string } {
  const one = words.length === 1 && !/s$/i.test(words[0]!.trim())
  return one ? { are: 'is', were: 'was' } : { are: 'are', were: 'were' }
}

// Dropby voice: warm, first person for what Liam does, "we" for the clean
// itself, plain text, no em dashes, signed "Kind regards, Lewis" (Lewis handles the bookings, Liam 2026-09-11).
//
// Every variant ends on the SAME question, with only the reason for writing
// changing above it: the apology still apologises and the cold one still says
// it has been a while, but none of them asks the customer to pick a date.
//
// settings takes its words from the signed-in business (businessSettings(),
// filled at boot from /api/me) — no Dropby fallback.
export function rebookMessage(p: RebookMessageParts, settings: BusinessSettings = businessSettings()): string {
  const first = firstName(p.name)
  const ask = dayQuestion(p.offer, p.offers)
  const sign = settings.booking_signoff
  // The one variant that does NOT end on the day question: after eight months
  // a date is presumptuous, so it asks the easy yes and promises to sort the
  // day afterwards (Liam's wording, 2026-09-16). Someone taken off the list
  // gets something off the first clean back (rebook_winback).
  if (p.kind === 'away' || p.kind === 'winback') {
    const month = monthLabel(p.lastClean, p.now)
    return wordMessage(p.kind === 'winback' ? 'rebook_winback' : 'rebook_away', { 'first name': first, 'last done': month ? `(${month})` : '', 'your name': sign }, settings)
  }
  const what = p.jobs?.length ? jobsPhrase(p.jobs) : 'windows'
  const verb = p.jobs?.length ? areFor(p.jobs) : { are: 'are', were: 'were' }
  if (p.kind === 'six_months') {
    return wordMessage('rebook_six_months', { 'first name': first, 'time since': timeSinceLabel(p.lastClean, p.now), jobs: what, 'day question': ask, 'your name': sign }, settings)
  }
  if (p.kind === 'cold') {
    const month = monthLabel(p.lastClean, p.now)
    return wordMessage('rebook_cold', { 'first name': first, jobs: what, 'last visit': month ? `(last visit ${month})` : '', 'day question': ask, 'your name': sign }, settings)
  }
  // 'upcoming' (due within the week) used to add "next week". Now that the
  // message names a day, that sentence can contradict the day beside it —
  // written on Monday 14th offering Wednesday 16th, it read "due for a clean
  // next week. Would Wednesday 16th September work for you?". The day says
  // when; the kind only decides how the note opens, and due-soon and due-now
  // open the same way. It still routes here so the caller keeps the
  // distinction for everything else that reads it.
  if (p.kind === 'overdue') {
    const month = monthLabel(p.due, p.now)
    return wordMessage('rebook_overdue', { 'first name': first, 'jobs were': `${what} ${verb.were}`, 'when due': month ? `back in ${month}` : 'a while ago', 'day question': ask, 'your name': sign }, settings)
  }
  return wordMessage('rebook_due', { 'first name': first, 'jobs are': `${what} ${verb.are}`, 'day question': ask, 'your name': sign }, settings)
}

// The second message, a week or more after the first with no reply: the
// customer is told they are coming off the regular list, kindly, with the
// door left open.
export function secondMessage(name: string | null | undefined, settings: BusinessSettings = businessSettings()): string {
  return wordMessage('rebook_second', { 'first name': firstName(name), 'your name': settings.booking_signoff }, settings)
}

// The final message, after the second gets no reply (Liam, 26 Oct 2026, Dropby's own "we will take you off our list"
// text): it says they are coming off, names when they were last done, and carries the reviews link when one is saved.
// Sending it takes them off the round.
export function finalMessage(name: string | null | undefined, lastClean: string | null | undefined, job: string | null | undefined, settings: BusinessSettings = businessSettings(), now: Date = new Date()): string {
  const month = monthLabel(lastClean, now)
  return wordMessage('rebook_final', { 'first name': firstName(name), jobs: (job?.trim() || 'windows').toLowerCase(), 'last done': month ? `(${month})` : '', 'your name': settings.booking_signoff }, settings)
}

// The second message about an extra job (several jobs, PLAN 4a): it speaks of that job only. The customer stays a
// customer, so it never says "take you off our regular list".
export function secondJobMessage(name: string | null | undefined, job: string, settings: BusinessSettings = businessSettings()): string {
  const first = firstName(name)
  const greeting = first ? `Hi ${first},` : 'Hi,'
  return `${greeting}\nHope you're okay. We've not heard back from you, so I'll leave the ${job} for now. Just message me whenever you'd like them done and I'll get you booked in.\nKind regards,\n${settings.booking_signoff}`
}

// ---- the ladder -------------------------------------------------------------
// to_message → (send 1) → waiting (7 days) → second → (send 2) → no_reply → (tap) lapsed
export type RebookStage = 'to_message' | 'waiting' | 'second' | 'no_reply'

export const SECOND_MESSAGE_AFTER_DAYS = 7

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00`)
  d.setDate(d.getDate() + days)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// The day the second message unlocks, or null when there is nothing to wait for.
export function secondMessageFrom(c: { rebook_messaged_on?: string | null }): string | null {
  return c.rebook_messaged_on ? addDays(c.rebook_messaged_on, SECOND_MESSAGE_AFTER_DAYS) : null
}

export function stageOf(c: { rebook_messages?: number | null; rebook_messaged_on?: string | null; last_clean?: string | null }, today: string): RebookStage {
  const n = messagesThisCycle(c)
  if (n === 0) return 'to_message'
  if (n >= NO_REPLY_AFTER) return 'no_reply'
  const from = secondMessageFrom(c)
  return from && today >= from ? 'second' : 'waiting'
}

// wa.me wants the number in international form with no plus: 07700 900123 →
// 447700900123. Imported phones are free text ("07700 900123 - NO WA",
// "+44 (0)7700 900123", "07700 900123 / 07700 900124"), so:
// - a "no WA" / "no whatsapp" note means no button at all;
// - only the FIRST number in the field is used;
// - "(0)" after the country code is the trunk zero and goes;
// - a UK number must be a mobile (447…): WhatsApp cannot reach a landline,
//   and a link to one would still count as a message sent.
// A number typed without its country gets the ROUND's (round-config.ts, Step 1): 44 in the UK,
// exactly as before; 1 in the US and Canada, where a 10-digit number has no leading 0.
export function waNumber(raw: string | null | undefined, code: '44' | '1' = callingCode()): string | null {
  if (!raw) return null
  if (/no\s*(wa|whats\s*app)\b/i.test(raw)) return null
  const first = /\+?\d[\d\s().-]*\d/.exec(raw.replace(/\(0\)/g, ''))
  const digits = cleanPhone(first ? first[0] : null)
  if (!digits) return null
  const n = internationalDigits(digits, code)
  if (!n) return null
  if (n.startsWith('44') && !n.startsWith('447')) return null
  return n.length >= 10 && n.length <= 15 ? n : null
}

// A phone number in international form, digits only ("447700900123", "14155550123"), from what
// was typed: "+…" and "00…" already carry their country; otherwise the round's is added (a UK
// "07…" loses its trunk 0; a US or Canadian "415…" gets 1 unless it already starts 1 with 11
// digits). Null when there is nothing to go on. What was typed is never changed in storage.
export function internationalDigits(typed: string, code: '44' | '1'): string | null {
  const plus = typed.startsWith('+')
  let n = typed.replace(/\D/g, '')
  if (!n) return null
  if (plus) { /* already international */ } else if (n.startsWith('00')) n = n.slice(2)
  else if (code === '44') { if (n.startsWith('0')) n = `44${n.slice(1)}` }
  else if (!(n.length === 11 && n.startsWith('1'))) n = `1${n}`
  if (n.startsWith('440')) n = `44${n.slice(3)}`
  return n
}

// The count that the row and the "No reply" rule use: messages sent THIS
// overdue cycle. A message sent before the customer's last clean belongs to
// an earlier cycle (they replied, got cleaned, and have now slipped again), so
// it does not count — and the next tap writes 1, which is the reset.
export function messagesThisCycle(c: { rebook_messages?: number | null; rebook_messaged_on?: string | null; last_clean?: string | null }): number {
  const n = Math.max(0, Math.round(Number(c.rebook_messages) || 0))
  if (!n) return 0
  if (c.rebook_messaged_on && c.last_clean && c.rebook_messaged_on <= c.last_clean) return 0
  return n
}

// True when the app is being read on a phone or tablet. Only the WhatsApp
// link cares, and it cares a lot (below).
export function onAPhone(): boolean {
  if (typeof navigator === 'undefined') return false
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
}

// Two different links for the same message, because wa.me is only the right
// one on a phone, where it opens the WhatsApp app straight into the chat.
//
// On a computer wa.me serves its own page first, asking whether to open the
// desktop app, so every single message costs a "don't use the app", then a
// "go to web", then a wait for WhatsApp Web to boot. Lewis, 19 Sep 2026:
// "it's probably like 10, 15 seconds wasted every time I want to click and
// send a message". web.whatsapp.com/send skips all of that and lands in the
// conversation with the text already written.
//
// The platform is read at call time and can be overridden, which is what the
// tests do — nothing here depends on a browser existing.
export function whatsappHref(raw: string | null | undefined, body: string, phone: boolean = onAPhone(), place: WhatsAppPlace = whatsappPlace()): string | null {
  const n = waNumber(raw)
  // A number that asked to stop WhatsApps from Roundly gets none, from anywhere (0081, business-settings.ts whatsappStopped).
  if (!n || whatsappStopped(n)) return null
  const text = encodeURIComponent(body)
  if (phone) return `https://wa.me/${n}?text=${text}`
  return place === 'app' ? `whatsapp://send?phone=${n}&text=${text}` : `https://web.whatsapp.com/send?phone=${n}&text=${text}`
}

// Where WhatsApp opens on a computer (Liam, 8 Oct 2026: every message opened another WhatsApp Web tab). WhatsApp
// Web cuts its tab off from the page that opened it, so no link can reuse that tab; the WhatsApp app has no tabs.
// A browser cannot tell whether the app is installed, so it is this computer's own choice, kept in this browser.
// Phones always open the app through wa.me.
export type WhatsAppPlace = 'web' | 'app'
const PLACE_KEY = 'roundly:whatsapp-on-computer'
let placeMemory: WhatsAppPlace | null = null
export function whatsappPlace(): WhatsAppPlace {
  if (placeMemory) return placeMemory
  try { if (typeof localStorage !== 'undefined' && localStorage.getItem(PLACE_KEY) === 'app') return 'app' } catch { /* Web is the default. */ }
  return 'web'
}
export function setWhatsappPlace(place: WhatsAppPlace): void {
  placeMemory = place
  try { localStorage.setItem(PLACE_KEY, place) } catch { /* Kept for this visit only. */ }
}
// A browser tab for WhatsApp Web; the app opens from the same page.
export const whatsappTarget = (href: string): '_blank' | '_self' => href.startsWith('https:') ? '_blank' : '_self'

// The row's own record of what has been sent: "Messaged 11 Sep", "Messaged
// twice · last 18 Sep". The date wording is the caller's (ui.shortDate) so the
// screen and the diary print dates the same way.
export function messagedLabel(count: number | null | undefined, on: string | null | undefined, shortDate: (iso: string) => string): string {
  const n = Math.max(0, Math.round(Number(count) || 0))
  if (n === 0) return ''
  const when = on ? shortDate(on) : ''
  if (n === 1) return when ? `Messaged ${when}` : 'Messaged once'
  const times = n === 2 ? 'twice' : `${n} times`
  return when ? `Messaged ${times} · last ${when}` : `Messaged ${times}`
}

// Workbook key: "Messaged at least 2 times" = not a customer anymore.
export const NO_REPLY_AFTER = 2

export function noReplyReady(count: number | null | undefined): boolean {
  return (Number(count) || 0) >= NO_REPLY_AFTER
}

// The line appended to the customer's contact note so the customer page and
// History say what happened even if the counter is later reset.
// `word` is how it went (contact.ts noteWord): WhatsApp, as every line before 0036 says, or text or email.
export function messageNoteLine(dayLabel: string, n: number, word: 'WhatsApp' | 'text' | 'email' = 'WhatsApp'): string {
  return `${dayLabel}: rebook ${word} ${n}`
}

// A contact-note line as the owner reads it on the Messages card: the stored line keeps its computer dates and short
// words (acceptedReplyDates reads them back, so they are never rewritten), shown as "8 Oct: first message on WhatsApp"
// and "8 Oct: offered 11 Oct, waiting for their reply" (bug hunt 8 Oct 2026, wording-brand #16 / messages #20).
export function contactLineWords(line: string, shortDate: (iso: string) => string): string {
  const nth = (n: number): string => (n === 1 ? 'first' : n === 2 ? 'second' : n === 3 ? 'third' : `${n}th`)
  const by = (way: string): string => (way === 'WhatsApp' ? 'on WhatsApp' : `by ${way}`)
  return line
    .replace(/: rebook (WhatsApp|text|email) (\d+)$/, (_m, way: string, n: string) => `: ${nth(Number(n))} message ${by(way)}`)
    .replace(/\b(\d{4}-\d{2}-\d{2})\b/g, (iso: string) => shortDate(iso))
    .replace(/; awaiting their (reply|choice)$/, ', waiting for their $1')
}

export function noReplyNoteLine(dayLabel: string, n: number): string {
  return `${dayLabel}: no reply after ${n} rebook message${n === 1 ? '' : 's'}, marked lapsed`
}

// A winback message PROMISES money off. Nothing else in the app remembers a
// promise, so it goes in the customer's own note, where the booking form and
// their page both show it — otherwise they come back, get charged full price,
// and the offer was worse than not making it.
export function winbackNoteLine(dayLabel: string, word: 'WhatsApp' | 'text' | 'email' = 'WhatsApp'): string {
  return `${dayLabel}: winback ${word}, ${WINBACK_DISCOUNT_PERCENT}% off first clean offered`
}

// Append, but never at the cost of what the owner typed: if the line will not
// fit under the server's 2000-character cap the note is returned UNCHANGED
// (the caller then leaves contact_note out of the save). The counter and the
// date still record the message; a note is the nice-to-have.
export function appendNote(existing: string | null | undefined, line: string, max = 2000): string {
  const base = (existing ?? '').trim()
  const next = base ? `${base}\n${line}` : line
  return next.length > max ? (existing ?? '') : next
}

// Recorded replies are not unanswered nudges. The job identity keeps an extra
// job's conversation from suppressing the customer's other due work.
export type ReplyGroup = 'datesoffered' | 'followup' | 'paused' | 'stopped' | 'replybooked'
type ReplyCustomer = Pick<import('./types').Customer, 'status' | 'message_reply' | 'message_reply_on' | 'message_reply_dates' | 'message_reply_job_id' | 'follow_up_on' | 'reply_agreed' | 'reply_done' | 'reply_answered' | 'message_replies' | 'reply_states'>
export function replyForJob<T extends ReplyCustomer>(c: T, jobId: number | null = null): T {
  if (c.message_reply && c.message_reply !== 'dates') return c
  let stored: Record<string, Partial<T>> = {}
  try { stored = JSON.parse(c.message_replies ?? '{}') } catch { /* older cached payload */ }
  const key = jobId == null ? 'main' : String(jobId)
  const reply = c.reply_states?.[key] ?? stored[key]
  if (reply) return { ...c, message_reply: null, reply_agreed: null, reply_done: null, reply_answered: null, ...reply, message_reply_job_id: jobId }
  if (!c.message_replies && !c.reply_states && (c.message_reply_job_id ?? null) === jobId) return c
  return { ...c, message_reply: null, message_reply_on: null, message_reply_dates: null, message_reply_job_id: jobId, reply_agreed: null, reply_done: null, reply_answered: null }
}
export function replyConversations<T extends ReplyCustomer>(c: T): T[] {
  if (c.status !== 'active' || c.message_reply && c.message_reply !== 'dates' || c.follow_up_on) return [c]
  let map: Record<string, unknown> = {}
  try { map = JSON.parse(c.message_replies ?? '{}') } catch { /* old payload */ }
  const keys = Object.keys(c.reply_states ?? map)
  return keys.length ? keys.map(key => replyForJob(c, key === 'main' ? null : Number(key))) : [c]
}
export function replyNeedsFollowup(c: ReplyCustomer, today: string): boolean {
  if (c.message_reply !== 'dates' || c.reply_agreed || c.reply_done || c.reply_answered) return false
  if (c.follow_up_on) return c.follow_up_on <= today
  const dates = replyDates(c)
  return !!(dates.length && dates.every(d => d < today)) || !!(c.message_reply_on && addDays(c.message_reply_on, 7) <= today)
}
export function replyGroup(c: ReplyCustomer, today: string): ReplyGroup | null {
  if (c.message_reply === 'stopped' && c.status === 'paused') return 'stopped'
  if (c.status === 'paused') return 'paused'
  if (c.status !== 'active') return null
  if (c.follow_up_on && c.follow_up_on > today) return 'followup'
  if (c.follow_up_on && c.follow_up_on <= today) return null
  if (c.message_reply !== 'dates') return null
  if (c.reply_agreed) return 'replybooked'
  // A completed visit since this reply resumes the ordinary cycle, including
  // a date moved earlier in the booking form than the dates first offered.
  if (c.reply_done) return null
  // A day was picked from these dates and that visit has since moved: answered, so back to the ordinary flow (where a
  // moved visit reads "Visit moved · tell them"), never "Dates offered" again (bug hunt 8 Oct 2026, diary #9).
  if (c.reply_answered) return null
  return 'datesoffered'
}
export function replyDates(c: Pick<ReplyCustomer, 'message_reply_dates'>): string[] {
  try { const dates: unknown = JSON.parse(c.message_reply_dates ?? '[]'); return Array.isArray(dates) ? dates.filter((d): d is string => typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)).sort() : [] } catch { return [] }
}
// Initial offers predate structured replies. Recover only the last machine-written
// main-job offer in the latest main-job message block; never infer dates from prose
// or another job's conversation. A date can still be chosen explicitly in the editor.
export function acceptedReplyDates(c: Pick<ReplyCustomer, 'message_reply_dates'> & { contact_note?: string | null; rebook_messaged_on?: string | null; message_reply_job_id?: number | null }, jobId: number | null): string[] {
  const structured = (c.message_reply_job_id ?? null) === jobId ? replyDates(c) : []
  if (structured.length) return structured
  if (jobId !== null || !c.rebook_messaged_on) return []
  const sent = /^(\d{4})-(\d{2})-(\d{2})$/.exec(c.rebook_messaged_on)
  if (!sent) return []
  // The day stamp exactly as the note line is written (ui.ts shortDate: "10 Sep", or "10 Sep 25" for another year),
  // never the browser's own short month, which is "Sept" in September (bug hunt 8 Oct 2026, wording-brand #3). "Sept"
  // lines written that way are read too.
  const day = Number(sent[3]), mon = SHORT_MONTHS[Number(sent[2]) - 1] ?? ''
  const stamps = new Set([`${day} ${mon}`, `${day} ${mon} ${sent[1]!.slice(2)}`, ...(mon === 'Sep' ? [`${day} Sept`, `${day} Sept ${sent[1]!.slice(2)}`] : [])])
  const stampOf = (line: string): string | null => { const m = /^([^:]+): /.exec(line); return m && stamps.has(m[1]!) ? m[1]! : null }
  const lines = (c.contact_note ?? '').split('\n')
  let result: string[] = []
  let mainBlock = false
  for (const line of lines) {
    if (/^.*: (?:rebook (?:WhatsApp|text|email) \d+|.+ message (?:WhatsApp|text|email) \d+)$/.test(line)) {
      if (/^.*: rebook (?:WhatsApp|text|email) \d+$/.test(line)) { mainBlock = stampOf(line) !== null; result = [] }
      else { mainBlock = false; result = [] }
    }
    const offer = /^([^:]+): offered (\d{4}-\d{2}-\d{2}(?: or \d{4}-\d{2}-\d{2})*); awaiting their (?:choice|reply)$/.exec(line)
    if (offer && mainBlock && stamps.has(offer[1]!)) {
      result = offer[2]!.split(' or ').filter(d => { const day = new Date(d + 'T12:00:00Z'); return !Number.isNaN(day.getTime()) && day.toISOString().slice(0,10) === d })
      mainBlock = false
    }
  }
  return [...new Set(result)].sort()
}

// The {time sentence} with no time: a round that gives no set times says the day is the plan (Liam, 10 Oct 2026); one
// that does says the time will follow.
function noTimeSentence(): string {
  return setTimesOn() ? "I'll let you know the time." : "We'll be with you during the day."
}

export function agreedTimeKey(date: string, slot: string | null | undefined): string | null {
  const time = slot?.trim()
  return date && time ? `${date}|${time}` : null
}
export function timeIsAgreed(visit: { date: string; time_slot?: string | null; confirmed?: number; time_agreed_for?: string | null }): boolean {
  // No set times (Your business): no time is shown, so none is agreed.
  const key = agreedTimeKey(visit.date, visibleTime(visit))
  return visit.confirmed === 1 && key !== null && visit.time_agreed_for === key
}

// Only saved visit fields form the outgoing draft. Internal visit notes remain
// private; the owner can add customer-facing wording in the editable message.
// Every day a customer reads is written one way, "Tuesday 13th October", as the offers write it (offerDay; bug hunt
// 8 Oct 2026, wording-brand #7): the confirmation and the visit moved message too.
export function acceptedVisitMessage(name: string, visit: { date: string; time_slot?: string | null; confirmed?: number; time_agreed_for?: string | null }, opts: {firstBooking?:boolean;otherJobsNotice?:string} = {}): string {
  const day = offerDay(visit.date)
  const signer = businessSettings().booking_signoff?.trim() || ''
  const shown = visibleTime(visit)
  const time = shown && messageClock(shown)
  const timeSentence = time ? timeIsAgreed(visit) ? `We're booked for ${time}.` : `I can come at ${time}.` : noTimeSentence()
  const words=wordMessage(visit.confirmed === 1 ? 'visit_confirmed' : 'visit_offer', { 'first name': firstName(name), day, 'time sentence': timeSentence, 'your name': signer })
  if(!opts.otherJobsNotice)return words
  return words.includes('\nKind regards,')?words.replace('\nKind regards,',`\n${opts.otherJobsNotice}\nKind regards,`):`${words}\n${opts.otherJobsNotice}`
}
// The "visit moved" message: an agreed visit was moved to another day, and the customer is asked whether the new day
// suits (Release 2; asks rather than tells, because a move resets the agreement).
export function visitMovedMessage(name: string, jobs: string, visit: { date: string; moved_from: string; time_slot?: string | null }): string {
  const shown = visibleTime(visit)
  const time = shown && messageClock(shown)
  const timeSentence = time ? `I can come at ${time}.` : noTimeSentence()
  return wordMessage('visit_moved', { 'first name': firstName(name), jobs, 'old day': offerDay(visit.moved_from), day: offerDay(visit.date), 'time sentence': timeSentence, 'your name': businessSettings().booking_signoff?.trim() || '' })
}

// A visit that couldn't be done (Liam, 22 Oct 2026; docs/plans/rearrange/PLAN.md). What Couldn't do it saved, as the
// customer reads it: the reason ('time' for Ran out of time, ui.ts blockedChoiceOf), and "today" or "on Thursday" for
// the day it was meant to be. Customer cancelled has nothing to be sorry for, so it reads as Other.
export interface MissedVisit { from: string; reason: string | null }
export function sorryLine(m: MissedVisit, today: string = roundDay()): string {
  const d = new Date(`${m.from}T12:00:00`)
  const when = m.from === today || Number.isNaN(d.getTime()) ? 'today' : `on ${DAYS[d.getDay()]}`
  if (m.reason === 'time') return `Sorry, we ran out of time ${when}.`
  if (m.reason === 'not_in') return `Sorry we missed you ${when}.`
  if (m.reason === 'weather') return `Sorry, the weather stopped us ${when}.`
  return `Sorry we couldn't get to you ${when}.`
}
// The owner's message once the visit is on its new day: asks if that day suits, as the visit moved message does.
export function missedVisitMessage(name: string, m: MissedVisit, visit: { date: string; time_slot?: string | null }, today: string = roundDay()): string {
  const shown = visibleTime(visit)
  const time = shown && messageClock(shown)
  const timeSentence = time ? `I can come at ${time}.` : noTimeSentence()
  return wordMessage('visit_missed', { 'first name': firstName(name), 'sorry line': sorryLine(m, today), day: offerDay(visit.date), 'time sentence': timeSentence, 'your name': businessSettings().booking_signoff?.trim() || '' })
}
// A worker's: no day, because only the owner can move a visit.
export function missedWorkerMessage(name: string, m: MissedVisit, businessName: string, today: string = roundDay()): string {
  return wordMessage('visit_missed_worker', { 'first name': firstName(name), 'sorry line': sorryLine(m, today), 'business name': businessName })
}
export function replyQuiet(c: ReplyCustomer, today: string, jobId: number | null = null): boolean {
  const group = replyGroup(replyForJob(c, jobId), today)
  return !!group && (!['datesoffered', 'replybooked'].includes(group) || (replyForJob(c, jobId).message_reply_job_id ?? null) === jobId)
}
// jobs: several jobs ("gutters and windows"), said before the question: "great! For the gutters and windows, would …".
export function datesReplyMessage(name: string, dates: string[], jobs?: string): string {
  if (!dates.length) return 'Choose at least one day to offer.'
  const days = dates.map(offerDay)
  const ask = days.length <= 2 ? `Would ${days.join(' or ')} work for you?` : `Would any of these dates work for you?\n${days.map(d => `• ${d}`).join('\n')}`
  const question = jobs ? `For the ${jobs}, ${ask.charAt(0).toLowerCase()}${ask.slice(1)}` : ask
  const signer = businessSettings().booking_signoff?.trim() || ''
  // "Great!" rather than "of course" (Liam, 8 Oct 2026): they have just said yes to a visit.
  return wordMessage('dates_reply', { 'first name': firstName(name), 'dates question': question, 'your name': signer })
}
